// Cave84 — Inventory Page (Design Overhaul)
// Dense table view matching cave84_inventory.html mockup exactly.
// 240px sticky filter sidebar + 10-column grouped table + pagination.

import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useUpdateWine } from '@/hooks/useWines';
import { useUpdateBottle } from '@/hooks/useBottles';
import { useCreateWishlistEntry } from '@/hooks/useWishlist';
import OpenBottleModal from '@/components/OpenBottleModal';
import type { WineStyle, ClassificationLevel, InventoryViewMode } from '@cave84/shared-types';
import type { Cellar, SavedView } from '@cave84/shared-types';
import {
  useGroupedInventory,
  DEFAULT_FILTERS,
  type DrinkingReadiness,
  type InventoryFilters,
  type GroupedInventoryRow,
  type FacetCounts,
} from '@/hooks/useInventory';
import {
  useSavedViews,
  useCreateSavedView,
  useDeleteSavedView,
} from '@/hooks/useSavedViews';
import { PivotView } from '@/components/inventory/PivotView';
import BulkActionBar from '@/components/inventory/BulkActionBar';
import BulkTransferModal from '@/components/inventory/BulkTransferModal';
import { Money } from '@/components/Money';
import { useIsOwner } from '@/stores/auth.store';

// ──────────────────────────────────────────────────────────────
// Constants
// ──────────────────────────────────────────────────────────────

const TABLE_COLS = '32px 2.6fr 0.7fr 0.85fr 0.55fr 1fr 0.75fr 0.7fr 0.65fr 0.75fr 28px';
const PAGE_SIZE = 25;

const DRINKING_LABEL: Record<DrinkingReadiness, string> = {
  too_young: 'Hold', developing: 'Developing', peak: 'Peak',
  declining: 'Drink Up', over_the_hill: 'Past Peak',
};

const DC: Record<DrinkingReadiness, { text: string; border: string; bg: string }> = {
  too_young:    { text: '#6B8190', border: 'rgba(107,129,144,0.4)', bg: 'rgba(107,129,144,0.04)' },
  developing:   { text: '#94A357', border: 'rgba(148,163,87,0.4)',  bg: 'rgba(148,163,87,0.04)'  },
  peak:         { text: '#C49255', border: 'rgba(196,146,85,0.4)',  bg: 'rgba(196,146,85,0.04)'  },
  declining:    { text: '#B8704A', border: 'rgba(184,112,74,0.4)',  bg: 'rgba(184,112,74,0.04)'  },
  over_the_hill:{ text: '#908977', border: 'rgba(92,85,77,0.4)',   bg: 'rgba(92,85,77,0.04)'    },
};

const STYLE_LABELS: Record<WineStyle, string> = {
  red: 'Red', white: 'White', rose: 'Rose', sparkling: 'Sparkling',
  fortified: 'Fortified', sweet: 'Sweet', orange: 'Orange',
};

const STYLE_DOT: Record<WineStyle, string> = {
  red: '#C49255', white: '#D4B896', rose: '#E8829A', sparkling: '#93C5D9',
  fortified: '#C47A2A', sweet: '#E5CE6A', orange: '#D4843A',
};

const FORMAT_LABELS: Record<string, string> = {
  half: '375ml', '750ml': '750ml', magnum: 'Magnum', double_magnum: 'D.Mag',
  jeroboam: 'Jeroboam', imperial: 'Imperial', salmanazar: 'Salmanazar',
  methuselah: 'Methuselah', other: 'Other',
};

const CLASS_LABEL: Partial<Record<ClassificationLevel, string>> = {
  grand_cru: 'Grand Cru', premier_cru: '1er Cru', first_growth: '1er GCC',
  second_growth: '2eme GCC', third_growth: '3eme GCC',
  fourth_growth: '4eme GCC', fifth_growth: '5eme GCC',
  ava: 'AVA', gi: 'GI',
};

const ALL_STYLES: WineStyle[] = ['red','white','rose','sparkling','fortified','sweet','orange'];
const ALL_DRINKING: DrinkingReadiness[] = ['peak','developing','too_young','declining','over_the_hill'];

// a11y: keyboard parity (Enter/Space) for a click-only element. Pair with a
// literal `role="button" tabIndex={0}` so jsx-a11y can see them statically —
// spread props are opaque to the linter.
const keyActivate = (fn: () => void) => (e: React.KeyboardEvent) => {
  if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); fn(); }
};

// a11y: focus an input once when it mounts (replaces the autoFocus prop).
const focusOnMount = (el: HTMLInputElement | null) => { el?.focus(); };

// ──────────────────────────────────────────────────────────────
// Cellar colour helper
// ──────────────────────────────────────────────────────────────

function cellarColors(c: Cellar) {
  if (c.is_home) return { text:'#C49255', border:'rgba(196,146,85,0.3)', bg:'rgba(196,146,85,0.04)', flag:'🏠' };
  const n = c.name.toLowerCase();
  if (n.includes('cyprus'))  return { text:'#4A8FA8', border:'rgba(74,143,168,0.45)',  bg:'rgba(74,143,168,0.06)',  flag:'🇨🇾' };
  if (n.includes('berlin'))  return { text:'#6B7480', border:'rgba(107,116,128,0.5)',  bg:'rgba(107,116,128,0.06)', flag:'🇩🇪' };
  if (n.includes('romania')) return { text:'#A87A5C', border:'rgba(168,122,92,0.45)',  bg:'rgba(168,122,92,0.06)',  flag:'🇷🇴' };
  if (n.includes('virtual')) return { text:'#8E6FA3', border:'rgba(142,111,163,0.45)', bg:'rgba(142,111,163,0.06)', flag:'📦' };
  return { text:'#6B7480', border:'rgba(107,116,128,0.5)', bg:'rgba(107,116,128,0.06)', flag:'🍷' };
}

// ──────────────────────────────────────────────────────────────
// Export
// ──────────────────────────────────────────────────────────────

/**
 * @param bom Prepend a UTF-8 BOM (U+FEFF). Required for CSV: a bare .csv carries
 * no encoding metadata, so Excel falls back to the machine's ANSI codepage and
 * renders "Château" as "ChÃ¢teau". Never set it for JSON — a BOM breaks strict
 * JSON.parse consumers.
 */
function downloadFile(content: string, filename: string, mime: string, bom = false) {
  const blob = new Blob([bom ? '\uFEFF' + content : content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

interface CsvOptions {
  filename?: string;
  /** Purchase cost is owner-only data — the bulk bar is reachable by viewers. */
  includeCost?: boolean;
}

function exportCsv(rows: GroupedInventoryRow[], opts: CsvOptions = {}) {
  const { filename = 'cave84_inventory.csv', includeCost = true } = opts;
  const cols = ['Producer', 'Wine', 'Vintage', 'Format', 'QTY', 'Cellar', 'Readiness'];
  if (includeCost) cols.push('Cost/btl (EUR)');
  const header = cols.map(c => `"${c}"`).join(',');
  const lines = rows.map(r => {
    const fields: (string | number)[] = [
      r.producer.name, r.wine.cuvee_name ?? '', r.vintage.year,
      FORMAT_LABELS[r.format] ?? r.format, r.qty, r.cellar.name,
      DRINKING_LABEL[r.drinkingStatus],
    ];
    if (includeCost) fields.push(r.avgCostEur != null ? r.avgCostEur : '');
    return fields.map(f => `"${String(f).replace(/"/g, '""')}"`).join(',');
  });
  // CRLF + BOM per RFC 4180 / Excel expectations.
  downloadFile([header, ...lines].join('\r\n'), filename, 'text/csv;charset=utf-8;', true);
}

function exportJson(rows: GroupedInventoryRow[]) {
  const data = rows.map(r => ({
    producer: r.producer.name,
    wine: r.wine.cuvee_name ?? null,
    vintage: r.vintage.year,
    format: FORMAT_LABELS[r.format] ?? r.format,
    qty: r.qty,
    cellar: r.cellar.name,
    region: r.region,
    readiness: DRINKING_LABEL[r.drinkingStatus],
    drinking_window: r.drinkingWindow,
    special_occasion: r.isSpecialOccasion,
    score: r.avgScore,
    avg_cost_eur: r.avgCostEur,
    market_price_eur: r.marketPrice,
  }));
  downloadFile(JSON.stringify(data, null, 2), 'cave84_inventory.json', 'application/json;charset=utf-8;');
}

// ──────────────────────────────────────────────────────────────
// Micro-components
// ──────────────────────────────────────────────────────────────

function CheckBox({ checked, indeterminate = false }: { checked: boolean; indeterminate?: boolean }) {
  const filled = checked || indeterminate;
  return (
    <div style={{
      width:13, height:13, border:`1px solid ${filled?'#C49255':'#4A453E'}`,
      borderRadius:2, display:'grid', placeItems:'center',
      background:filled?'#C49255':'transparent', flexShrink:0,
    }}>
      {indeterminate ? (
        <span style={{ width:7, height:1.5, background:'#0F0E0C', borderRadius:1 }}/>
      ) : checked && (
        <svg width="7" height="5" viewBox="0 0 7 5" fill="none">
          <path d="M1 2.5L2.8 4L6 1" stroke="#0F0E0C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      )}
    </div>
  );
}

function CellarBadge({ cellar }: { cellar: Cellar }) {
  const c = cellarColors(cellar);
  return (
    <span style={{
      display:'inline-flex', alignItems:'center', gap:5,
      padding:'3px 8px', fontSize:11, fontWeight:600,
      textTransform:'uppercase', letterSpacing:'0.08em',
      borderRadius:2, border:`1px solid ${c.border}`, background:c.bg, color:c.text,
      whiteSpace:'nowrap',
    }}>
      {c.flag} {cellar.name}
    </span>
  );
}

function DrinkingCell({ status, win }: { status: DrinkingReadiness; win: { from: number; to: number } }) {
  const c = DC[status];
  const hasWin = win.from > 0 && win.to > 0;
  return (
    <div style={{ display:'flex', flexDirection:'column', alignItems:'flex-start', gap:4 }}>
      <span style={{
        display:'inline-flex', alignItems:'center', gap:5,
        padding:'3px 8px', fontSize:11, fontWeight:600,
        textTransform:'uppercase', letterSpacing:'0.06em',
        borderRadius:2, border:`1px solid ${c.border}`, background:c.bg, color:c.text, whiteSpace:'nowrap',
      }}>
        <span style={{ width:5, height:5, borderRadius:'50%', background:c.text, flexShrink:0 }}/>
        {DRINKING_LABEL[status]}
      </span>
      {hasWin && (
        <span style={{ fontFamily:"'JetBrains Mono',monospace", fontSize:11.5, color:'#908977' }}>
          {win.from}&ndash;{win.to}
        </span>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────
// Save View modal
// ──────────────────────────────────────────────────────────────

function SaveViewModal({
  onSave, onClose,
}: {
  onSave: (name: string) => void;
  onClose: () => void;
}) {
  const [name, setName] = useState('');

  const handle = () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    onSave(trimmed);
  };

  return (
    <>
      {/* Backdrop */}
      <div aria-label="Close" style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.6)', zIndex:100 }} onClick={onClose} role="button" tabIndex={0} onKeyDown={keyActivate(onClose)}/>
      {/* Dialog */}
      <div style={{ position:'fixed', top:'50%', left:'50%', transform:'translate(-50%,-50%)', zIndex:101, background:'#221F1A', border:'1px solid #3F3A35', borderRadius:6, padding:28, width:340 }}>
        <div style={{ fontFamily:"'Fraunces',serif", fontSize:20, color:'#ECE6D4', marginBottom:16 }}>Save Current View</div>
        <input
          ref={focusOnMount}
          type="text"
          value={name}
          onChange={e => setName(e.target.value)}
          onKeyDown={e => { if (e.key==='Enter') handle(); if (e.key==='Escape') onClose(); }}
          placeholder="View name…"
          style={{ width:'100%', background:'#1B202B', border:'1px solid #4A453E', borderRadius:3, padding:'9px 12px', color:'#ECE6D4', fontSize:14, outline:'none', boxSizing:'border-box', marginBottom:16 }}
        />
        <div style={{ display:'flex', justifyContent:'flex-end', gap:8 }}>
          <button type="button" onClick={onClose} style={{ padding:'8px 16px', border:'1px solid #3A3530', borderRadius:3, background:'transparent', color:'#B8B0A2', fontSize:13, cursor:'pointer' }}>Cancel</button>
          <button type="button" onClick={handle} disabled={!name.trim()} style={{ padding:'8px 16px', border:'none', borderRadius:3, background:'#C49255', color:'#ECE6D4', fontSize:13, cursor:name.trim()?'pointer':'default', opacity:name.trim()?1:0.45 }}>Save</button>
        </div>
      </div>
    </>
  );
}

// ──────────────────────────────────────────────────────────────
// Saved Views bar
// ──────────────────────────────────────────────────────────────

function SavedViewsBar({
  views,
  activeViewId,
  onLoad,
  onDelete,
  onSaveNew,
}: {
  views: SavedView[];
  activeViewId: string | null;
  onLoad: (v: SavedView) => void;
  onDelete: (id: string) => void;
  onSaveNew: () => void;
}) {
  return (
    <div style={{ display:'flex', alignItems:'center', gap:8, padding:'10px 0', overflowX:'auto', flexWrap:'nowrap' }}>
      <span style={{ fontSize:11, textTransform:'uppercase', letterSpacing:'0.16em', color:'#908977', fontWeight:600, whiteSpace:'nowrap', flexShrink:0 }}>Views</span>
      {views.map(v => {
        const active = v.id === activeViewId;
        return (
          <div key={v.id} style={{ display:'inline-flex', alignItems:'center', gap:0, borderRadius:4, border:`1px solid ${active?'rgba(196,146,85,0.4)':'#3A3530'}`, background:active?'rgba(196,146,85,0.06)':'#1B202B', overflow:'hidden', flexShrink:0 }}>
            <button type="button" onClick={() => onLoad(v)} style={{ padding:'5px 12px', background:'transparent', border:'none', color:active?'#C49255':'#B8B0A2', fontSize:12, cursor:'pointer', fontWeight:active?600:400, whiteSpace:'nowrap' }}>
              {v.name}
            </button>
            <button type="button" onClick={e => { e.stopPropagation(); onDelete(v.id); }} style={{ padding:'5px 8px 5px 4px', background:'transparent', border:'none', borderLeft:`1px solid ${active?'rgba(196,146,85,0.2)':'#3A3530'}`, color:'#908977', fontSize:14, cursor:'pointer', lineHeight:1 }}>&#215;</button>
          </div>
        );
      })}
      <button type="button" onClick={onSaveNew} style={{ display:'inline-flex', alignItems:'center', gap:6, padding:'5px 11px', border:'1px dashed #3A3530', borderRadius:4, background:'transparent', color:'#9C9483', fontSize:12, cursor:'pointer', whiteSpace:'nowrap', flexShrink:0 }}>
        <span style={{ fontSize:14, lineHeight:1 }}>+</span> Save view
      </button>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────
// Filter sidebar
// ──────────────────────────────────────────────────────────────

function FilterSidebar({ filters, onChange, facets, cellars }: {
  filters: InventoryFilters; onChange: (f: InventoryFilters) => void;
  facets: FacetCounts; cellars: Cellar[];
}) {
  const set = useCallback((p: Partial<InventoryFilters>) => onChange({ ...filters, ...p }), [filters, onChange]);

  function toggle<T>(arr: T[], val: T): T[] {
    return arr.includes(val) ? arr.filter(x => x !== val) : [...arr, val];
  }

  const SH = ({ label, count }: { label: string; count?: number }) => (
    <div style={{ fontSize:11, textTransform:'uppercase', letterSpacing:'0.16em', color:'#9C9483', fontWeight:600, marginBottom:12, display:'flex', justifyContent:'space-between' }}>
      <span>{label}</span>
      {count !== undefined && <span style={{ fontFamily:"'JetBrains Mono',monospace", letterSpacing:0, color:'#908977' }}>{count}</span>}
    </div>
  );

  const FR = ({ id, label, checked, count, onToggle, dot }: {
    id: string; label: string; checked: boolean; count: number; onToggle: () => void; dot?: string;
  }) => (
    <div key={id} onClick={onToggle} role="button" tabIndex={0} onKeyDown={keyActivate(onToggle)} aria-pressed={checked} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'5px 0', cursor:'pointer', color:checked?'#ECE6D4':'#B8B0A2', fontSize:13 }}>
      <div style={{ display:'flex', alignItems:'center', gap:9 }}>
        <CheckBox checked={checked}/>
        {dot && <span style={{ width:7, height:7, borderRadius:'50%', background:dot, flexShrink:0 }}/>}
        <span>{label}</span>
      </div>
      <span style={{ fontFamily:"'JetBrains Mono',monospace", fontSize:11, color:'#908977' }}>{count}</span>
    </div>
  );

  const regions = Array.from(facets.byRegion.entries()).sort((a,b) => b[1]-a[1]);
  const iS: React.CSSProperties = { flex:1, background:'#1B202B', border:'1px solid #232936', borderRadius:3, padding:'5px 8px', color:'#ECE6D4', fontSize:12, fontFamily:"'JetBrains Mono',monospace", outline:'none', width:0 };

  return (
    <aside style={{ position:'sticky', top:80, alignSelf:'start', maxHeight:'calc(100vh - 100px)', overflowY:'auto' }}>

      <div style={{ borderBottom:'1px solid #232936', padding:'4px 0 14px' }}>
        <SH label="Cellar" count={cellars.length}/>
        {cellars.map(c => <FR key={c.id} id={c.id} label={c.name} checked={filters.cellarIds.includes(c.id)} count={facets.byCellar.get(c.id)??0} onToggle={() => set({ cellarIds: toggle(filters.cellarIds, c.id) })}/>)}
      </div>

      <div style={{ borderBottom:'1px solid #232936', padding:'14px 0' }}>
        <SH label="Region" count={regions.length}/>
        {regions.map(([r,n]) => <FR key={r} id={r} label={r} checked={filters.regions.includes(r)} count={n} onToggle={() => set({ regions: toggle(filters.regions, r) })}/>)}
      </div>

      <div style={{ borderBottom:'1px solid #232936', padding:'14px 0' }}>
        <SH label="Style" count={ALL_STYLES.length}/>
        {ALL_STYLES.map(s => <FR key={s} id={s} label={STYLE_LABELS[s]} checked={filters.styles.includes(s)} count={facets.byStyle.get(s)??0} onToggle={() => set({ styles: toggle(filters.styles, s) })} dot={STYLE_DOT[s]}/>)}
      </div>

      <div style={{ borderBottom:'1px solid #232936', padding:'14px 0' }}>
        <SH label="Vintage"/>
        <div style={{ display:'flex', gap:8 }}>
          <input type="number" placeholder="From" value={filters.vintageFrom??''} onChange={e => set({ vintageFrom: e.target.value ? Number(e.target.value) : null })} style={iS}
            onFocus={e => { (e.target as HTMLInputElement).style.borderColor='#C49255'; }}
            onBlur={e => { (e.target as HTMLInputElement).style.borderColor='#3A3530'; }}/>
          <input type="number" placeholder="To" value={filters.vintageTo??''} onChange={e => set({ vintageTo: e.target.value ? Number(e.target.value) : null })} style={iS}
            onFocus={e => { (e.target as HTMLInputElement).style.borderColor='#C49255'; }}
            onBlur={e => { (e.target as HTMLInputElement).style.borderColor='#3A3530'; }}/>
        </div>
      </div>

      <div style={{ borderBottom:'1px solid #232936', padding:'14px 0' }}>
        <SH label="Readiness"/>
        {ALL_DRINKING.map(d => <FR key={d} id={d} label={DRINKING_LABEL[d]} checked={filters.drinkingStatuses.includes(d)} count={facets.byDrinkingStatus.get(d)??0} onToggle={() => set({ drinkingStatuses: toggle(filters.drinkingStatuses, d) })} dot={DC[d].text}/>)}
      </div>

      <div style={{ padding:'14px 0' }}>
        <SH label="More"/>
        <div onClick={() => set({ specialOccasion: !filters.specialOccasion })} role="button" tabIndex={0} onKeyDown={keyActivate(() => set({ specialOccasion: !filters.specialOccasion }))} aria-pressed={filters.specialOccasion} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'5px 0', cursor:'pointer', color:filters.specialOccasion?'#ECE6D4':'#B8B0A2', fontSize:13 }}>
          <div style={{ display:'flex', alignItems:'center', gap:9 }}>
            <CheckBox checked={filters.specialOccasion}/>
            <span>&#9733; Special Occasion</span>
          </div>
          <span style={{ fontFamily:"'JetBrains Mono',monospace", fontSize:11, color:'#908977' }}>{facets.specialOccasionCount}</span>
        </div>
      </div>
    </aside>
  );
}

// ──────────────────────────────────────────────────────────────
// Active filter pills
// ──────────────────────────────────────────────────────────────

function ActivePills({ filters, onChange, cellars }: {
  filters: InventoryFilters; onChange: (f: InventoryFilters) => void; cellars: Cellar[];
}) {
  const pills: { label: string; onRemove: () => void; gold?: boolean }[] = [];
  for (const id of filters.cellarIds) {
    const name = cellars.find(c => c.id === id)?.name ?? id;
    pills.push({ label: name, onRemove: () => onChange({ ...filters, cellarIds: filters.cellarIds.filter(x => x !== id) }) });
  }
  for (const r of filters.regions) pills.push({ label: r, onRemove: () => onChange({ ...filters, regions: filters.regions.filter(x => x !== r) }) });
  for (const s of filters.styles) pills.push({ label: STYLE_LABELS[s], onRemove: () => onChange({ ...filters, styles: filters.styles.filter(x => x !== s) }) });
  for (const d of filters.drinkingStatuses) pills.push({ label: DRINKING_LABEL[d], onRemove: () => onChange({ ...filters, drinkingStatuses: filters.drinkingStatuses.filter(x => x !== d) }) });
  if (filters.vintageFrom != null || filters.vintageTo != null) pills.push({ label: `${filters.vintageFrom??'…'}-${filters.vintageTo??'…'}`, onRemove: () => onChange({ ...filters, vintageFrom:null, vintageTo:null }) });
  if (filters.specialOccasion) pills.push({ label: '★ Special Occasion', gold: true, onRemove: () => onChange({ ...filters, specialOccasion: false }) });
  return (
    <>
      {pills.map((p, i) => (
        <div key={i} style={{ background:p.gold?'rgba(196,146,85,0.04)':'#1B202B', border:`1px solid ${p.gold?'rgba(196,146,85,0.3)':'#3A3530'}`, borderRadius:4, padding:'7px 12px', color:p.gold?'#C49255':'#B8B0A2', fontSize:12, display:'inline-flex', alignItems:'center', gap:8 }}>
          {p.label}
          <span onClick={e => { e.stopPropagation(); p.onRemove(); }} role="button" tabIndex={0} onKeyDown={keyActivate(p.onRemove)} aria-label="Remove filter" style={{ color:'#908977', fontSize:14, lineHeight:1, cursor:'pointer' }}>&#215;</span>
        </div>
      ))}
    </>
  );
}

// ──────────────────────────────────────────────────────────────
// Table row
// ──────────────────────────────────────────────────────────────

function InventoryRow({ row, selected, onToggleSelect }: {
  row: GroupedInventoryRow; selected: boolean; onToggleSelect: (k: string) => void;
}) {
  const navigate = useNavigate();
  const isOwner = useIsOwner();
  const { wine, vintage, producer, cellar, format, qty, drinkingStatus, drinkingWindow, isSpecialOccasion, avgScore, marketPrice, avgCostEur, appellation } = row;
  const classLabel = appellation ? (CLASS_LABEL[appellation.classification_level] ?? null) : null;

  const [menuOpen, setMenuOpen] = useState(false);
  const [editNameOpen, setEditNameOpen] = useState(false);
  const [editNameValue, setEditNameValue] = useState('');
  const [openBottleModalOpen, setOpenBottleModalOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const updateWine = useUpdateWine();
  const updateBottle = useUpdateBottle();
  const createWishlistEntry = useCreateWishlistEntry();

  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuOpen]);


  const handleToggleSpecialOccasion = async () => {
    setMenuOpen(false);
    const newValue = !isSpecialOccasion;
    for (const b of row.bottles) {
      await updateBottle.mutateAsync({
        id: b.id,
        cellarId: b.current_cellar_id,
        vintageId: b.vintage_id,
        patch: { is_special_occasion: newValue },
      });
    }
  };

  const handleAddToWishlist = () => {
    setMenuOpen(false);
    createWishlistEntry.mutate({
      wine_id: wine.id,
      vintage_year: vintage.year,
      priority: 'medium',
    });
  };


  return (
    <>
      <div
        style={{
          display:'grid', gridTemplateColumns:TABLE_COLS, gap:14, padding:'16px 20px',
          borderBottom:'1px solid #232936', alignItems:'center', cursor:'pointer', transition:'background 0.15s',
          background: isSpecialOccasion ? 'rgba(196,146,85,0.05)' : 'transparent',
          borderInlineStart: isSpecialOccasion ? '2px solid rgba(196,146,85,0.5)' : '2px solid transparent',
        }}
        onMouseEnter={e => { (e.currentTarget as HTMLDivElement).style.background = isSpecialOccasion ? 'rgba(196,146,85,0.09)' : '#1B202B'; }}
        onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.background = isSpecialOccasion ? 'rgba(196,146,85,0.05)' : 'transparent'; }}
        onClick={() => navigate(`/wines/${wine.id}?vintage=${vintage.year}`)}
        role="button" tabIndex={0} onKeyDown={keyActivate(() => navigate(`/wines/${wine.id}?vintage=${vintage.year}`))}
      >
        <div style={{ display:'grid', placeItems:'center', width:18, height:18 }} onClick={e => { e.stopPropagation(); onToggleSelect(row.key); }} role="button" tabIndex={0} onKeyDown={keyActivate(() => onToggleSelect(row.key))} aria-label="Select row">
          {/* Always the checkbox — the special-occasion ★ used to replace it
              here, which made flagged rows look unselectable. The ★ now sits
              beside the producer name, same as the mobile card. */}
          <CheckBox checked={selected}/>
        </div>

        <div>
          <div style={{ display:'flex', alignItems:'center', gap:7, fontFamily:"'Fraunces',serif", fontSize:16, fontWeight:500, color:'#ECE6D4', letterSpacing:'-0.01em', lineHeight:1.25 }}>
            {isSpecialOccasion && <span title="Special occasion" aria-label="Special occasion" style={{ color:'#C49255', fontSize:13, lineHeight:1, flexShrink:0, fontFamily:'Inter,sans-serif' }}>★</span>}
            <span>{producer.name}</span>
          </div>
          <div style={{ fontSize:12, color:'#9C9483', marginTop:3, display:'flex', alignItems:'center', gap:8 }}>
            <span>{wine.cuvee_name??'—'}</span>
            {classLabel && <span style={{ fontSize:11, color:'#C49255', textTransform:'uppercase', letterSpacing:'0.1em', fontWeight:600, padding:'1px 5px', border:'1px solid rgba(196,146,85,0.35)', borderRadius:1 }}>{classLabel}</span>}
          </div>
        </div>

        <div style={{ fontFamily:"'JetBrains Mono',monospace", fontSize:14, fontWeight:500, color:'#B8B0A2' }}>{vintage.year}</div>

        <div style={{ fontSize:12, color:'#9C9483' }}>{FORMAT_LABELS[format]??format}</div>

        <div style={{ textAlign:'center' }}>
          <div style={{ fontFamily:"'Fraunces',serif", fontSize:22, fontWeight:500, color:'#ECE6D4', lineHeight:1, letterSpacing:'-0.02em' }}>{qty}</div>
          <div style={{ fontSize:11, color:'#908977', textTransform:'uppercase', letterSpacing:'0.1em', marginTop:3, fontWeight:600 }}>BTL</div>
        </div>

        <div><CellarBadge cellar={cellar}/></div>

        <div><DrinkingCell status={drinkingStatus} win={drinkingWindow}/></div>

        <div style={{ display:'flex', flexDirection:'column', alignItems:'flex-end', gap:2 }}>
          {avgScore != null
            ? <><span style={{ fontFamily:"'Fraunces',serif", fontSize:18, fontWeight:500, color:'#C49255', lineHeight:1 }}>{avgScore}</span><span style={{ fontSize:11, color:'#908977', textTransform:'uppercase', letterSpacing:'0.08em' }}>pts</span></>
            : <span style={{ color:'#908977', fontSize:13 }}>—</span>}
        </div>

        <div style={{ textAlign:'right' }}>
          {isOwner && avgCostEur != null
            ? <div style={{ fontFamily:"'JetBrains Mono',monospace", fontSize:13, color:'#B8B0A2', fontWeight:500 }}><Money value={avgCostEur} currency="EUR" minWidthCh={7} forceVisible /></div>
            : <span style={{ color:'#908977', fontSize:13 }}>—</span>}
        </div>

        <div style={{ textAlign:'right' }}>
          {marketPrice != null
            ? <div style={{ fontFamily:"'JetBrains Mono',monospace", fontSize:13, color:'#ECE6D4', fontWeight:500 }}><Money value={marketPrice} currency="EUR" minWidthCh={7} forceVisible /></div>
            : <span style={{ color:'#908977', fontSize:13 }}>—</span>}
        </div>

        {/* Three-dot menu */}
        {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events -- non-interactive wrapper; onClick only isolates the row click from the inner button */}
        <div ref={menuRef} style={{ position:'relative', textAlign:'center' }} onClick={e => e.stopPropagation()}>
          <button
            type="button"
            onClick={() => setMenuOpen(v => !v)}
            style={{ background:'none', border:'none', cursor:'pointer', color:'#908977', padding:'4px 6px', borderRadius:3, lineHeight:1, display:'grid', placeItems:'center' }}
            onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.color='#B8B0A2'; (e.currentTarget as HTMLButtonElement).style.background='#232936'; }}
            onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.color='#908977'; (e.currentTarget as HTMLButtonElement).style.background='none'; }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="5"  r="1.2" fill="currentColor"/>
              <circle cx="12" cy="12" r="1.2" fill="currentColor"/>
              <circle cx="12" cy="19" r="1.2" fill="currentColor"/>
            </svg>
          </button>

          {menuOpen && (
            <div style={{ position:'absolute', right:0, top:'calc(100% + 4px)', background:'#1E1B17', border:'1px solid #3A3530', borderRadius:4, minWidth:160, zIndex:50, boxShadow:'0 8px 24px rgba(0,0,0,0.5)' }}>
              <button
                type="button"
                onClick={() => { setMenuOpen(false); navigate(`/inventory/${row.bottles[0]?.id ?? ''}`); }}
                style={{ width:'100%', textAlign:'left', padding:'10px 14px', fontSize:13, color:'#B8B0A2', background:'transparent', border:'none', cursor:'pointer', display:'flex', alignItems:'center', gap:8 }}
                onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background='#1B202B'; }}
                onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background='transparent'; }}
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
                </svg>
                View bottle
              </button>
              {isOwner && (
                <>
                  <button
                    type="button"
                    onClick={() => { setMenuOpen(false); setOpenBottleModalOpen(true); }}
                    style={{ width:'100%', textAlign:'left', padding:'10px 14px', fontSize:13, color:'#B8B0A2', background:'transparent', border:'none', borderTop:'1px solid #232936', cursor:'pointer', display:'flex', alignItems:'center', gap:8 }}
                    onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background='#1B202B'; }}
                    onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background='transparent'; }}
                  >
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>
                    </svg>
                    Open bottle
                  </button>
                  <button
                    type="button"
                    onClick={() => { void handleToggleSpecialOccasion(); }}
                    style={{ width:'100%', textAlign:'left', padding:'10px 14px', fontSize:13, color: isSpecialOccasion ? '#C49255' : '#B8B0A2', background:'transparent', border:'none', borderTop:'1px solid #232936', cursor:'pointer', display:'flex', alignItems:'center', gap:8 }}
                    onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background='#1B202B'; }}
                    onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background='transparent'; }}
                  >
                    <svg width="13" height="13" viewBox="0 0 24 24" fill={isSpecialOccasion ? '#C49255' : 'none'} stroke={isSpecialOccasion ? '#C49255' : 'currentColor'} strokeWidth="1.8">
                      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
                    </svg>
                    {isSpecialOccasion ? 'Remove from Special' : 'Add to Special Occasions'}
                  </button>
                  <button
                    type="button"
                    onClick={handleAddToWishlist}
                    style={{ width:'100%', textAlign:'left', padding:'10px 14px', fontSize:13, color:'#B8B0A2', background:'transparent', border:'none', borderTop:'1px solid #232936', cursor:'pointer', display:'flex', alignItems:'center', gap:8 }}
                    onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background='#1B202B'; }}
                    onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background='transparent'; }}
                  >
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                    </svg>
                    Add to Wishlist
                  </button>
                  <button
                    type="button"
                    onClick={() => { setMenuOpen(false); setEditNameValue(wine.cuvee_name ?? ''); setEditNameOpen(true); }}
                    style={{ width:'100%', textAlign:'left', padding:'10px 14px', fontSize:13, color:'#B8B0A2', background:'transparent', border:'none', borderTop:'1px solid #232936', cursor:'pointer', display:'flex', alignItems:'center', gap:8 }}
                    onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background='#1B202B'; }}
                    onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background='transparent'; }}
                  >
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                    </svg>
                    Edit name
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {editNameOpen && isOwner && (
        <>
          <div aria-label="Close" style={{ position:'fixed', inset:0, zIndex:50, background:'rgba(0,0,0,0.7)' }} onClick={() => setEditNameOpen(false)} role="button" tabIndex={0} onKeyDown={keyActivate(() => setEditNameOpen(false))}/>
          <div style={{ position:'fixed', left:'50%', top:'50%', zIndex:50, transform:'translate(-50%,-50%)', width:'calc(100vw - 32px)', maxWidth:400, boxSizing:'border-box', background:'#1E1B17', border:'1px solid #3A3530', borderRadius:6, padding:32, boxShadow:'0 16px 48px rgba(0,0,0,0.6)' }}>
            <h3 style={{ fontFamily:"'Fraunces',serif", fontSize:20, fontWeight:500, color:'#ECE6D4', marginBottom:4 }}>Edit Cuvée Name</h3>
            <p style={{ fontSize:12, color:'#908977', marginBottom:20 }}>{producer.name}</p>
            <input
              type="text"
              value={editNameValue}
              onChange={e => setEditNameValue(e.target.value)}
              ref={focusOnMount}
              style={{ width:'100%', background:'#232936', border:'1px solid #3A3530', borderRadius:4, padding:'8px 12px', fontSize:13, color:'#ECE6D4', outline:'none', marginBottom:24, boxSizing:'border-box' }}
              placeholder="e.g. Vosne-Romanée 1er Cru Les Suchots"
              onKeyDown={e => {
                if (e.key === 'Enter' && editNameValue.trim()) updateWine.mutate({ id: wine.id, patch: { cuvee_name: editNameValue.trim() } }, { onSuccess: () => setEditNameOpen(false) });
                if (e.key === 'Escape') setEditNameOpen(false);
              }}
            />
            <div style={{ display:'flex', justifyContent:'flex-end', gap:10 }}>
              <button type="button" onClick={() => setEditNameOpen(false)} disabled={updateWine.isPending}
                style={{ background:'transparent', border:'1px solid #3A3530', borderRadius:4, padding:'8px 16px', fontSize:13, color:'#B8B0A2', cursor:'pointer' }}>
                Cancel
              </button>
              <button type="button"
                onClick={() => updateWine.mutate({ id: wine.id, patch: { cuvee_name: editNameValue.trim() } }, { onSuccess: () => setEditNameOpen(false) })}
                disabled={updateWine.isPending || !editNameValue.trim()}
                style={{ background:'#C49255', border:'none', borderRadius:4, padding:'8px 16px', fontSize:13, color:'#fff', cursor: (updateWine.isPending || !editNameValue.trim()) ? 'default' : 'pointer', opacity: (updateWine.isPending || !editNameValue.trim()) ? 0.6 : 1 }}>
                {updateWine.isPending ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </>
      )}


      {openBottleModalOpen && row.bottles[0] && (
        <OpenBottleModal
          bottle={row.bottles[0]}
          onClose={() => setOpenBottleModalOpen(false)}
        />
      )}
    </>
  );
}

// ──────────────────────────────────────────────────────────────
// Mobile card — replaces table row on small screens
// ──────────────────────────────────────────────────────────────

function MobileInventoryCard({ row, selected, onToggleSelect }: {
  row: GroupedInventoryRow; selected: boolean; onToggleSelect: (k: string) => void;
}) {
  const navigate = useNavigate();
  const isOwner = useIsOwner();
  const { wine, vintage, producer, cellar, format, qty, drinkingStatus, drinkingWindow, isSpecialOccasion, marketPrice, avgCostEur } = row;
  const dc = DC[drinkingStatus];
  const yearStr = vintage.year === 1001 ? 'NV' : String(vintage.year);
  const windowStr = `${drinkingWindow.from}–${drinkingWindow.to}`;

  const [menuOpen, setMenuOpen] = useState(false);
  const [editNameOpen, setEditNameOpen] = useState(false);
  const [editNameValue, setEditNameValue] = useState('');
  const [openBottleModalOpen, setOpenBottleModalOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const updateWine = useUpdateWine();
  const updateBottle = useUpdateBottle();
  const createWishlistEntry = useCreateWishlistEntry();

  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuOpen]);


  const handleToggleSpecialOccasion = async () => {
    setMenuOpen(false);
    const newValue = !isSpecialOccasion;
    for (const b of row.bottles) {
      await updateBottle.mutateAsync({
        id: b.id,
        cellarId: b.current_cellar_id,
        vintageId: b.vintage_id,
        patch: { is_special_occasion: newValue },
      });
    }
  };

  const handleAddToWishlist = () => {
    setMenuOpen(false);
    createWishlistEntry.mutate({
      wine_id: wine.id,
      vintage_year: vintage.year,
      priority: 'medium',
    });
  };


  return (
    <>
      <div
        style={{
          borderBottom: '1px solid #232936', padding: '14px 16px',
          background: isSpecialOccasion ? 'rgba(196,146,85,0.05)' : 'transparent',
          borderInlineStart: isSpecialOccasion ? '2px solid rgba(196,146,85,0.5)' : '2px solid transparent',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 4 }}>
          {/* Selection target sized for touch, not for the 13px box it contains */}
          <button
            type="button"
            onClick={() => onToggleSelect(row.key)}
            aria-label={selected ? 'Deselect row' : 'Select row'}
            aria-pressed={selected}
            style={{ background:'none', border:'none', paddingBlock:6, paddingInlineStart:0, paddingInlineEnd:8, marginTop:-2, display:'grid', placeItems:'center', cursor:'pointer', flexShrink:0 }}
          >
            <CheckBox checked={selected}/>
          </button>
          <Link
            to={`/wines/${wine.id}?vintage=${vintage.year}`}
            style={{ textDecoration: 'none', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, flex: '1 1 auto', minWidth: 0 }}
          >
            <div style={{ minWidth: 0, flex: '1 1 auto' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                {isSpecialOccasion && <span style={{ color: '#C49255', fontSize: 11, flexShrink: 0 }}>★</span>}
                <span style={{ fontFamily: "'Fraunces',serif", fontSize: 15, fontWeight: 500, color: '#ECE6D4', letterSpacing: '-0.01em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block', minWidth: 0, flex: '1 1 auto' }}>
                  {producer.name}
                </span>
              </div>
              <div style={{ fontSize: 12, color: '#9C9483', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {wine.cuvee_name ?? '—'} · {FORMAT_LABELS[format] ?? format}
              </div>
              <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 12, color: '#B8B0A2' }}>{yearStr}</span>
                <span style={{ color: '#3A3530' }}>·</span>
                <CellarBadge cellar={cellar} />
                <span style={{ color: '#3A3530' }}>·</span>
                <span style={{ fontSize: 11, padding: '2px 6px', border: `1px solid ${dc.border}`, borderRadius: 100, color: dc.text, fontWeight: 500, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                  {DRINKING_LABEL[drinkingStatus]}
                </span>
              </div>
            </div>
            <div style={{ textAlign: 'right', flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
              <div style={{ fontFamily: "'Fraunces',serif", fontSize: 20, fontWeight: 500, color: '#ECE6D4', lineHeight: 1 }}>
                {qty}<span style={{ fontSize: 11, color: '#908977', marginLeft: 3, textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: 600 }}>btl</span>
              </div>
              {marketPrice != null && (
                <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 12, color: '#ECE6D4', marginTop: 4 }}><Money value={marketPrice} currency="EUR" minWidthCh={7} forceVisible /></div>
              )}
              {isOwner && avgCostEur != null && (
                <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 11, color: '#908977', marginTop: 2 }}><Money value={avgCostEur} currency="EUR" minWidthCh={7} forceVisible /> cost</div>
              )}
            </div>
          </Link>

          {/* Three-dot menu — dedicated column, not an overlay, so it can't collide with the price text */}
          <div ref={menuRef} style={{ position: 'relative', flexShrink: 0, marginTop: -6, marginRight: -8 }}>
            <button
              type="button"
              onClick={() => setMenuOpen(v => !v)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9C9483', padding: '10px 8px', borderRadius: 3, lineHeight: 1, display: 'grid', placeItems: 'center' }}
              aria-label="Row actions"
            >
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="5" r="1.6" fill="currentColor" />
                <circle cx="12" cy="12" r="1.6" fill="currentColor" />
                <circle cx="12" cy="19" r="1.6" fill="currentColor" />
              </svg>
            </button>

            {menuOpen && (
              <div style={{ position: 'absolute', right: 0, top: 'calc(100% + 4px)', background: '#1E1B17', border: '1px solid #3A3530', borderRadius: 4, minWidth: 180, zIndex: 50, boxShadow: '0 8px 24px rgba(0,0,0,0.5)' }}>
                <button
                  type="button"
                  onClick={() => { setMenuOpen(false); navigate(`/inventory/${row.bottles[0]?.id ?? ''}`); }}
                  style={{ width: '100%', textAlign: 'left', padding: '11px 14px', fontSize: 13, color: '#B8B0A2', background: 'transparent', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" />
                  </svg>
                  View bottle
                </button>
                {isOwner && (
                  <>
                    <button
                      type="button"
                      onClick={() => { setMenuOpen(false); setOpenBottleModalOpen(true); }}
                      style={{ width: '100%', textAlign: 'left', padding: '11px 14px', fontSize: 13, color: '#B8B0A2', background: 'transparent', border: 'none', borderTop: '1px solid #232936', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}
                    >
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" />
                      </svg>
                      Open bottle
                    </button>
                    <button
                      type="button"
                      onClick={() => { void handleToggleSpecialOccasion(); }}
                      style={{ width: '100%', textAlign: 'left', padding: '11px 14px', fontSize: 13, color: isSpecialOccasion ? '#C49255' : '#B8B0A2', background: 'transparent', border: 'none', borderTop: '1px solid #232936', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}
                    >
                      <svg width="13" height="13" viewBox="0 0 24 24" fill={isSpecialOccasion ? '#C49255' : 'none'} stroke={isSpecialOccasion ? '#C49255' : 'currentColor'} strokeWidth="1.8">
                        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                      </svg>
                      {isSpecialOccasion ? 'Remove from Special' : 'Add to Special Occasions'}
                    </button>
                    <button
                      type="button"
                      onClick={handleAddToWishlist}
                      style={{ width: '100%', textAlign: 'left', padding: '11px 14px', fontSize: 13, color: '#B8B0A2', background: 'transparent', border: 'none', borderTop: '1px solid #232936', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}
                    >
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                      </svg>
                      Add to Wishlist
                    </button>
                    <button
                      type="button"
                      onClick={() => { setMenuOpen(false); setEditNameValue(wine.cuvee_name ?? ''); setEditNameOpen(true); }}
                      style={{ width: '100%', textAlign: 'left', padding: '11px 14px', fontSize: 13, color: '#B8B0A2', background: 'transparent', border: 'none', borderTop: '1px solid #232936', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}
                    >
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                      </svg>
                      Edit name
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
        <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 11, color: '#908977', marginTop: 5 }}>window {windowStr}</div>
      </div>

      {editNameOpen && isOwner && (
        <>
          <div aria-label="Close" style={{ position: 'fixed', inset: 0, zIndex: 50, background: 'rgba(0,0,0,0.7)' }} onClick={() => setEditNameOpen(false)} role="button" tabIndex={0} onKeyDown={keyActivate(() => setEditNameOpen(false))} />
          <div style={{ position: 'fixed', left: '50%', top: '50%', zIndex: 50, transform: 'translate(-50%,-50%)', width: 'min(400px, calc(100vw - 32px))', background: '#1E1B17', border: '1px solid #3A3530', borderRadius: 6, padding: 24, boxShadow: '0 16px 48px rgba(0,0,0,0.6)' }}>
            <h3 style={{ fontFamily: "'Fraunces',serif", fontSize: 20, fontWeight: 500, color: '#ECE6D4', marginBottom: 4 }}>Edit Cuvée Name</h3>
            <p style={{ fontSize: 12, color: '#908977', marginBottom: 20 }}>{producer.name}</p>
            <input
              type="text"
              value={editNameValue}
              onChange={e => setEditNameValue(e.target.value)}
              ref={focusOnMount}
              style={{ width: '100%', background: '#232936', border: '1px solid #3A3530', borderRadius: 4, padding: '8px 12px', fontSize: 13, color: '#ECE6D4', outline: 'none', marginBottom: 24, boxSizing: 'border-box' }}
              placeholder="e.g. Vosne-Romanée 1er Cru Les Suchots"
              onKeyDown={e => {
                if (e.key === 'Enter' && editNameValue.trim()) updateWine.mutate({ id: wine.id, patch: { cuvee_name: editNameValue.trim() } }, { onSuccess: () => setEditNameOpen(false) });
                if (e.key === 'Escape') setEditNameOpen(false);
              }}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button type="button" onClick={() => setEditNameOpen(false)} disabled={updateWine.isPending}
                style={{ background: 'transparent', border: '1px solid #3A3530', borderRadius: 4, padding: '8px 16px', fontSize: 13, color: '#B8B0A2', cursor: 'pointer' }}>
                Cancel
              </button>
              <button type="button"
                onClick={() => updateWine.mutate({ id: wine.id, patch: { cuvee_name: editNameValue.trim() } }, { onSuccess: () => setEditNameOpen(false) })}
                disabled={updateWine.isPending || !editNameValue.trim()}
                style={{ background: '#C49255', border: 'none', borderRadius: 4, padding: '8px 16px', fontSize: 13, color: '#fff', cursor: (updateWine.isPending || !editNameValue.trim()) ? 'default' : 'pointer', opacity: (updateWine.isPending || !editNameValue.trim()) ? 0.6 : 1 }}>
                {updateWine.isPending ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </>
      )}


      {openBottleModalOpen && row.bottles[0] && (
        <OpenBottleModal
          bottle={row.bottles[0]}
          onClose={() => setOpenBottleModalOpen(false)}
        />
      )}
    </>
  );
}

// ──────────────────────────────────────────────────────────────
// Filter drawer — wraps FilterSidebar in a mobile bottom sheet
// ──────────────────────────────────────────────────────────────

function FilterDrawer({ open, onClose, filters, onChange, facets, cellars }: {
  open: boolean;
  onClose: () => void;
  filters: InventoryFilters;
  onChange: (f: InventoryFilters) => void;
  facets: FacetCounts;
  cellars: Cellar[];
}) {
  if (!open) return null;
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 200 }}>
      <div aria-label="Close" style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.6)' }} onClick={onClose} role="button" tabIndex={0} onKeyDown={keyActivate(onClose)} />
      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: '#141821', borderTop: '1px solid #3A3530', borderRadius: '14px 14px 0 0', maxHeight: '80vh', display: 'flex', flexDirection: 'column', paddingBottom: 'env(safe-area-inset-bottom)' }}>
        <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 10, paddingBottom: 4, flexShrink: 0 }}>
          <span style={{ width: 36, height: 4, borderRadius: 2, background: '#3A3530', display: 'block' }} />
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 20px 12px', borderBottom: '1px solid #232936', flexShrink: 0 }}>
          <span style={{ fontSize: 15, fontWeight: 600, color: '#ECE6D4' }}>Filters</span>
          <button type="button" onClick={onClose} style={{ background: '#C49255', border: 'none', borderRadius: 4, color: '#141821', padding: '6px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Done</button>
        </div>
        <div style={{ overflowY: 'auto', padding: '0 20px 20px', flex: 1 }}>
          <FilterSidebar filters={filters} onChange={onChange} facets={facets} cellars={cellars} />
        </div>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────
// Page
// ──────────────────────────────────────────────────────────────

export default function InventoryPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  const isOwner = useIsOwner();
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  // Seed filters from navigation state (e.g. clicking a Dashboard maturity-band
  // segment passes { drinkingStatuses } so Inventory opens pre-filtered).
  const [filters, setFilters] = useState<InventoryFilters>(() => {
    const navState = location.state as { drinkingStatuses?: DrinkingReadiness[] } | null;
    if (navState?.drinkingStatuses?.length) {
      return { ...DEFAULT_FILTERS, drinkingStatuses: navState.drinkingStatuses };
    }
    return DEFAULT_FILTERS;
  });
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [showExport, setShowExport] = useState(false);
  const [viewMode, setViewMode] = useState<InventoryViewMode>('list');
  const [activeViewId, setActiveViewId] = useState<string | null>(null);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showBulkTransfer, setShowBulkTransfer] = useState(false);
  const [bulkNotice, setBulkNotice] = useState<string | null>(null);

  const { grouped, stats, facets, cellars, isLoading } = useGroupedInventory(filters);
  const { data: savedViews = [] } = useSavedViews();
  const createView  = useCreateSavedView();
  const deleteView  = useDeleteSavedView();

  // Selection keys are row keys from the *current* filtered set. Changing the
  // filters can retire a key, so drop the selection rather than silently acting
  // on rows the user can no longer see.
  const clearSelection = useCallback(() => { setSelected(new Set()); setBulkNotice(null); }, []);

  const handleFilterChange = useCallback((f: InventoryFilters) => {
    setFilters(f); setPage(1); setActiveViewId(null); clearSelection();
  }, [clearSelection]);

  const handleLoadView = useCallback((v: SavedView) => {
    setFilters(v.filters as unknown as InventoryFilters);
    setViewMode(v.view_mode);
    setActiveViewId(v.id);
    setPage(1);
    clearSelection();
  }, [clearSelection]);

  const handleSaveView = useCallback((name: string) => {
    createView.mutate({
      name,
      filters,
      sortBy: filters.sortBy,
      viewMode,
    }, {
      onSuccess: (id) => { setActiveViewId(id); setShowSaveModal(false); },
    });
  }, [filters, viewMode, createView]);

  const handleDeleteView = useCallback((id: string) => {
    deleteView.mutate(id, {
      onSuccess: () => { if (activeViewId === id) setActiveViewId(null); },
    });
  }, [activeViewId, deleteView]);

  const totalPages = Math.max(1, Math.ceil(grouped.length / PAGE_SIZE));
  const pageRows   = useMemo(() => grouped.slice((page-1)*PAGE_SIZE, page*PAGE_SIZE), [grouped, page]);

  function toggleSelect(key: string) {
    setBulkNotice(null);
    setSelected(s => { const n = new Set(s); if (n.has(key)) n.delete(key); else n.add(key); return n; });
  }

  // Resolve against the full filtered set, not pageRows — a selection made on
  // page 1 must survive paging to page 2.
  const selectedRows = useMemo(
    () => grouped.filter(r => selected.has(r.key)),
    [grouped, selected]
  );

  const pageSelectedCount = pageRows.filter(r => selected.has(r.key)).length;
  const allPageSelected = pageRows.length > 0 && pageSelectedCount === pageRows.length;

  function toggleSelectPage() {
    setBulkNotice(null);
    setSelected(s => {
      const n = new Set(s);
      if (allPageSelected) pageRows.forEach(r => n.delete(r.key));
      else pageRows.forEach(r => n.add(r.key));
      return n;
    });
  }

  function handleExportSelection() {
    exportCsv(selectedRows, { filename: 'cave84_selection.csv', includeCost: isOwner });
    setBulkNotice(`${selectedRows.length} ${selectedRows.length === 1 ? 'row' : 'rows'} exported`);
  }

  const hasActiveFilters = filters.cellarIds.length>0 || filters.regions.length>0 || filters.styles.length>0 ||
    filters.drinkingStatuses.length>0 || filters.vintageFrom!=null || filters.vintageTo!=null || filters.specialOccasion;

  const pageNums = useMemo<(number|'...')[]>(() => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i+1);
    const r: (number|'...')[] = [1];
    if (page > 3) r.push('...');
    for (let i = Math.max(2,page-1); i <= Math.min(totalPages-1,page+1); i++) r.push(i);
    if (page < totalPages-2) r.push('...');
    r.push(totalPages);
    return r;
  }, [page, totalPages]);

  const B: React.CSSProperties = { background:'#1B202B', border:'1px solid #4A453E', color:'#ECE6D4', padding:'8px 14px', borderRadius:4, fontFamily:'Inter,sans-serif', fontSize:13, fontWeight:500, cursor:'pointer', display:'inline-flex', alignItems:'center', gap:8 };

  return (
    <div style={{ background:'#141821', minHeight:'100vh', overflowX:'hidden' }}>
      <div style={{ maxWidth:1440, margin:'0 auto', padding: isMobile ? '0 16px' : '0 32px', position:'relative', zIndex:2 }}>

        {/* Page header */}
        <section style={{ padding: isMobile ? '20px 0 16px' : '36px 0 24px', display:'flex', alignItems:'flex-end', justifyContent:'space-between', gap:16, borderBottom:'1px solid #232936', flexWrap:'wrap' }}>
          <div>
            <div style={{ fontSize:11, color:'#9C9483', textTransform:'uppercase', letterSpacing:'0.18em', marginBottom:8, fontWeight:500 }}>Cave84 / Inventory</div>
            <h1 style={{ fontFamily:"'Fraunces',serif", fontSize: isMobile ? 28 : 38, fontWeight:400, letterSpacing:'-0.02em', lineHeight:1, color:'#ECE6D4', margin:0 }}>The Cellar</h1>
            <div style={{ color:'#9C9483', fontSize:13, marginTop:8, display:'flex', gap:14, alignItems:'center' }}>
              <span>{stats.total} bottles &middot; {stats.cellarCount} cellars</span>
              {stats.unpricedCount > 0 && (
                <>
                  <span style={{ width:2, height:2, borderRadius:'50%', background:'#908977', flexShrink:0 }}/>
                  <span style={{ color:'#9C9483', fontStyle:'italic' }}>{stats.unpricedCount} unpriced</span>
                </>
              )}
            </div>
          </div>
          <div style={{ display:'flex', gap:8, flexShrink:0 }}>
            {/* Export includes per-bottle purchase cost — Owner-only, same as
                the Dashboard/Inventory cost figures. */}
            {!isMobile && isOwner && <><div style={{ position:'relative' }}>
              <button type="button" onClick={() => setShowExport(v => !v)} style={B}>Export</button>
              {showExport && (
                <>
                  <div aria-label="Close" style={{ position:'fixed', inset:0, zIndex:10 }} onClick={() => setShowExport(false)} role="button" tabIndex={0} onKeyDown={keyActivate(() => setShowExport(false))}/>
                  <div style={{ position:'absolute', right:0, top:'100%', marginTop:4, background:'#1B202B', border:'1px solid #3F3A35', borderRadius:4, overflow:'hidden', zIndex:20, width:140 }}>
                    {[{ l:'Export CSV', fn:() => { exportCsv(grouped); setShowExport(false); } },
                      { l:'Export JSON', fn:() => { exportJson(grouped); setShowExport(false); } }].map((it,i) => (
                      <button key={it.l} type="button" onClick={it.fn} style={{ width:'100%', textAlign:'left', padding:'10px 14px', fontSize:13, color:'#ECE6D4', background:'transparent', border:'none', borderTop:i>0?'1px solid #232936':'none', cursor:'pointer' }}
                        onMouseEnter={e => { (e.currentTarget).style.background='#1B202B'; }}
                        onMouseLeave={e => { (e.currentTarget).style.background='transparent'; }}>
                        {it.l}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
            {/* Bulk Transfer is selection-driven — it lives in BulkActionBar,
                which only appears once rows are checked. */}</> }
            {isOwner && (
              <button type="button" onClick={() => navigate('/inventory/add')} style={{ ...B, background:'#C49255', border:'1px solid #C49255' }}>{isMobile ? '+ Add' : '+ Add Bottle'}</button>
            )}
          </div>
        </section>

        {/* Saved Views bar */}
        {savedViews.length > 0 && (
          <SavedViewsBar
            views={savedViews}
            activeViewId={activeViewId}
            onLoad={handleLoadView}
            onDelete={handleDeleteView}
            onSaveNew={() => setShowSaveModal(true)}
          />
        )}

        {/* Toolbar — mobile: compact filter+search bar; desktop: full controls */}
        {isMobile ? (
          <div style={{ display:'flex', alignItems:'center', gap:10, padding:'12px 0', borderBottom:'1px solid #232936', flexWrap:'wrap' }}>
            <button type="button" onClick={() => setFilterDrawerOpen(true)} style={{ display:'inline-flex', alignItems:'center', gap:6, padding:'8px 14px', background: hasActiveFilters ? 'rgba(196,146,85,0.1)' : '#1B202B', border: `1px solid ${hasActiveFilters ? 'rgba(196,146,85,0.4)' : '#3A3530'}`, borderRadius:4, color: hasActiveFilters ? '#C49255' : '#B8B0A2', fontSize:13, fontWeight:500, cursor:'pointer' }}>
              <svg width="14" height="12" viewBox="0 0 14 12" fill="none"><path d="M1 1h12M3 6h8M5 11h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
              Filters{hasActiveFilters ? ' ·' : ''}
            </button>
            <div style={{ flex:1, background:'#1B202B', border:'1px solid #232936', borderRadius:4, padding:'7px 10px', display:'flex', alignItems:'center', gap:6, color:'#9C9483', minWidth:0 }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8}><circle cx="11" cy="11" r="7"/><path d="m20 20-3.5-3.5"/></svg>
              <input type="search" value={filters.search} onChange={e => handleFilterChange({ ...filters, search: e.target.value })} placeholder="Search..." style={{ background:'transparent', border:'none', outline:'none', color:'#ECE6D4', fontSize:13, flex:1, minWidth:0 }}/>
            </div>
            <select value={filters.sortBy} onChange={e => handleFilterChange({ ...filters, sortBy: e.target.value as InventoryFilters['sortBy'] })} style={{ background:'#1B202B', border:'1px solid #3F3A35', borderRadius:4, color:'#ECE6D4', padding:'8px 10px', fontSize:12, cursor:'pointer', outline:'none' }}>
              <option value="drinking_window">Window</option>
              <option value="producer">Producer</option>
              <option value="vintage_asc">Vintage ↑</option>
              <option value="vintage_desc">Vintage ↓</option>
              <option value="price_asc">Price ↑</option>
              <option value="price_desc">Price ↓</option>
            </select>
          </div>
        ) : (
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'18px 0', gap:20, flexWrap:'wrap' }}>
          <div style={{ display:'flex', alignItems:'center', gap:16, flexWrap:'wrap' }}>
            <ActivePills filters={filters} onChange={handleFilterChange} cellars={cellars}/>
            {hasActiveFilters && <button type="button" onClick={() => handleFilterChange(DEFAULT_FILTERS)} style={{ fontSize:12, color:'#9C9483', background:'none', border:'none', cursor:'pointer' }}>Clear all</button>}
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:12 }}>
            {/* View mode switcher */}
            <div style={{ display:'flex', border:'1px solid #3A3530', borderRadius:4, overflow:'hidden' }}>
              {(['list','pivot'] as InventoryViewMode[]).map(mode => (
                <button key={mode} type="button"
                  onClick={() => setViewMode(mode)}
                  title={mode === 'list' ? 'List view' : 'Pivot view'}
                  style={{ padding:'7px 11px', background:viewMode===mode?'#1B202B':'transparent', border:'none', borderLeft:mode==='pivot'?'1px solid #3A3530':'none', color:viewMode===mode?'#C49255':'#908977', cursor:'pointer', display:'grid', placeItems:'center' }}>
                  {mode === 'list' ? (
                    <svg width="14" height="12" viewBox="0 0 14 12" fill="none">
                      <rect x="0" y="0" width="14" height="2" rx="1" fill="currentColor"/>
                      <rect x="0" y="5" width="14" height="2" rx="1" fill="currentColor"/>
                      <rect x="0" y="10" width="14" height="2" rx="1" fill="currentColor"/>
                    </svg>
                  ) : (
                    <svg width="14" height="12" viewBox="0 0 14 12" fill="none">
                      <rect x="0" y="0" width="6" height="5" rx="1" fill="currentColor"/>
                      <rect x="8" y="0" width="6" height="5" rx="1" fill="currentColor"/>
                      <rect x="0" y="7" width="6" height="5" rx="1" fill="currentColor"/>
                      <rect x="8" y="7" width="6" height="5" rx="1" fill="currentColor"/>
                    </svg>
                  )}
                </button>
              ))}
            </div>
            {/* Save view button when no saved views bar is shown yet */}
            {savedViews.length === 0 && (
              <button type="button" onClick={() => setShowSaveModal(true)} style={{ padding:'7px 12px', border:'1px dashed #3A3530', borderRadius:4, background:'transparent', color:'#9C9483', fontSize:12, cursor:'pointer' }}>
                + Save view
              </button>
            )}
            <div style={{ background:'#1B202B', border:'1px solid #232936', borderRadius:4, padding:'7px 12px', display:'flex', alignItems:'center', gap:8, color:'#9C9483' }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8}><circle cx="11" cy="11" r="7"/><path d="m20 20-3.5-3.5"/></svg>
              <input type="search" value={filters.search} onChange={e => handleFilterChange({ ...filters, search: e.target.value })} placeholder="Search..." style={{ background:'transparent', border:'none', outline:'none', color:'#ECE6D4', fontSize:13, width:130 }}/>
            </div>
            <select value={filters.sortBy} onChange={e => handleFilterChange({ ...filters, sortBy: e.target.value as InventoryFilters['sortBy'] })} style={{ background:'#1B202B', border:'1px solid #3F3A35', borderRadius:4, color:'#ECE6D4', padding:'8px 14px', fontSize:13, fontWeight:500, cursor:'pointer', outline:'none' }}>
              <option value="drinking_window">Sort: Drinking Window</option>
              <option value="producer">Sort: Producer</option>
              <option value="vintage_asc">Sort: Vintage &#x2191;</option>
              <option value="vintage_desc">Sort: Vintage &#x2193;</option>
              <option value="price_asc">Sort: Market Price &#x2191;</option>
              <option value="price_desc">Sort: Market Price &#x2193;</option>
            </select>
          </div>
        </div>
        )}

        {/* Layout: sidebar (desktop) + main area */}
        <div style={{ display:'grid', gridTemplateColumns: isMobile ? '1fr' : '240px 1fr', gap:32, padding:'16px 0 80px' }}>
          {!isMobile && <FilterSidebar filters={filters} onChange={handleFilterChange} facets={facets} cellars={cellars}/>}

          <main style={{ background: viewMode === 'pivot' ? 'transparent' : '#221F1A', border: viewMode === 'pivot' ? 'none' : '1px solid #3A3530', minWidth: 0 }}>
            {viewMode === 'pivot' && (
              <PivotView
                rows={grouped}
                onCellClick={(region, style) => handleFilterChange({ ...DEFAULT_FILTERS, regions: [region], styles: [style] })}
              />
            )}
            {viewMode === 'list' && (
              <>
                {isLoading ? (
                  <div style={{ padding:'64px 20px', textAlign:'center', color:'#908977', fontSize:14 }}>Loading inventory...</div>
                ) : grouped.length === 0 ? (
                  <div style={{ padding:'64px 20px', textAlign:'center' }}>
                    <p style={{ color:'#908977', fontSize:14, marginBottom:12 }}>No bottles match these filters</p>
                    <button type="button" onClick={() => handleFilterChange(DEFAULT_FILTERS)} style={{ color:'#C49255', background:'none', border:'none', fontSize:13, cursor:'pointer' }}>Reset filters</button>
                  </div>
                ) : (
                  isMobile ? (
                    <>
                      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:12, padding:'10px 16px', borderBottom:'1px solid #232936' }}>
                        <span style={{ fontFamily:"'JetBrains Mono',monospace", fontSize:11, color:'#908977' }}>
                          {grouped.length} {grouped.length === 1 ? 'entry' : 'entries'}
                        </span>
                        <button
                          type="button"
                          onClick={toggleSelectPage}
                          style={{ display:'inline-flex', alignItems:'center', gap:6, background:'none', border:'none', padding:0, fontSize:11.5, color: pageSelectedCount > 0 ? '#C49255' : '#908977', cursor:'pointer' }}
                        >
                          <CheckBox checked={allPageSelected} indeterminate={!allPageSelected && pageSelectedCount > 0}/>
                          {allPageSelected ? 'Deselect page' : 'Select page'}
                        </button>
                      </div>
                      {pageRows.map(row => (
                        <MobileInventoryCard key={row.key} row={row} selected={selected.has(row.key)} onToggleSelect={toggleSelect}/>
                      ))}
                      {totalPages > 1 && (
                        <div style={{ padding:'12px 16px', display:'flex', justifyContent:'space-between', alignItems:'center', borderTop:'1px solid #3F3A35' }}>
                          <button type="button" onClick={() => setPage(p => Math.max(1,p-1))} disabled={page===1} style={{ padding:'8px 16px', border:'1px solid #232936', borderRadius:4, background:'#1B202B', color:page===1?'#4A453E':'#B8B0A2', cursor:page===1?'default':'pointer', fontSize:13 }}>← Prev</button>
                          <span style={{ fontFamily:"'JetBrains Mono',monospace", fontSize:12, color:'#9C9483' }}>{page} / {totalPages}</span>
                          <button type="button" onClick={() => setPage(p => Math.min(totalPages,p+1))} disabled={page===totalPages} style={{ padding:'8px 16px', border:'1px solid #232936', borderRadius:4, background:'#1B202B', color:page===totalPages?'#4A453E':'#B8B0A2', cursor:page===totalPages?'default':'pointer', fontSize:13 }}>Next →</button>
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      {/* Table header */}
                      <div style={{ display:'grid', gridTemplateColumns:TABLE_COLS, gap:14, padding:'14px 20px', borderBottom:'1px solid #3F3A35', fontSize:11, textTransform:'uppercase', letterSpacing:'0.14em', color:'#9C9483', fontWeight:600, alignItems:'center' }}>
                        <button
                          type="button"
                          onClick={toggleSelectPage}
                          aria-label={allPageSelected ? 'Deselect all rows on this page' : 'Select all rows on this page'}
                          aria-pressed={allPageSelected}
                          style={{ background:'none', border:'none', padding:0, display:'grid', placeItems:'center', width:18, height:18, cursor:'pointer' }}
                        >
                          <CheckBox checked={allPageSelected} indeterminate={!allPageSelected && pageSelectedCount > 0}/>
                        </button>
                        <div>Producer / Wine</div><div>Vintage</div><div>Format</div>
                        <div style={{ textAlign:'center' }}>Qty</div><div>Cellar</div><div>Readiness</div>
                        <div style={{ textAlign:'right' }}>Score</div>
                        <div style={{ textAlign:'right' }}>Cost/btl</div>
                        <div style={{ textAlign:'right' }}>Market</div><div/>
                      </div>

                      {pageRows.map(row => (
                        <InventoryRow key={row.key} row={row} selected={selected.has(row.key)} onToggleSelect={toggleSelect}/>
                      ))}

                      {/* Pagination */}
                      <div style={{ padding:'16px 20px', display:'flex', justifyContent:'space-between', alignItems:'center', borderTop:'1px solid #3F3A35', fontSize:12 }}>
                        <span style={{ fontFamily:"'JetBrains Mono',monospace", fontSize:12, color:'#B8B0A2' }}>
                          {(page-1)*PAGE_SIZE+1}&ndash;{Math.min(page*PAGE_SIZE, grouped.length)} of {grouped.length} groups
                        </span>
                        <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                          <button type="button" onClick={() => setPage(p => Math.max(1,p-1))} disabled={page===1}
                            style={{ width:28, height:28, border:'1px solid #232936', borderRadius:3, display:'grid', placeItems:'center', background:'#1B202B', color:page===1?'#4A453E':'#B8B0A2', cursor:page===1?'default':'pointer', fontSize:12 }}>&#8249;</button>
                          {pageNums.map((n,i) => n==='...'
                            ? <span key={`e${i}`} style={{ color:'#908977' }}>...</span>
                            : <button key={n} type="button" onClick={() => setPage(n as number)}
                                style={{ width:28, height:28, border:`1px solid ${n===page?'#C49255':'#3A3530'}`, borderRadius:3, display:'grid', placeItems:'center', background:n===page?'#C49255':'#1B202B', color:n===page?'#ECE6D4':'#B8B0A2', cursor:'pointer', fontSize:12 }}>{n}</button>
                          )}
                          <button type="button" onClick={() => setPage(p => Math.min(totalPages,p+1))} disabled={page===totalPages}
                            style={{ width:28, height:28, border:'1px solid #232936', borderRadius:3, display:'grid', placeItems:'center', background:'#1B202B', color:page===totalPages?'#4A453E':'#B8B0A2', cursor:page===totalPages?'default':'pointer', fontSize:12 }}>&#8250;</button>
                        </div>
                      </div>
                    </>
                  )
                )}
              </>
            )}
          </main>
        </div>
      </div>

      {/* Filter drawer — mobile only */}
      <FilterDrawer
        open={filterDrawerOpen}
        onClose={() => setFilterDrawerOpen(false)}
        filters={filters}
        onChange={handleFilterChange}
        facets={facets}
        cellars={cellars}
      />

      {/* Save view modal */}
      {showSaveModal && (
        <SaveViewModal
          onSave={handleSaveView}
          onClose={() => setShowSaveModal(false)}
        />
      )}

      {/* Bulk actions — only rendered in list view, where the checkboxes live */}
      {viewMode === 'list' && (
        <BulkActionBar
          rows={selectedRows}
          isOwner={isOwner}
          isMobile={isMobile}
          notice={bulkNotice}
          onNotice={setBulkNotice}
          onClear={clearSelection}
          onTransfer={() => setShowBulkTransfer(true)}
          onExport={handleExportSelection}
        />
      )}

      {showBulkTransfer && isOwner && selectedRows.length > 0 && (
        <BulkTransferModal
          rows={selectedRows}
          onClose={() => setShowBulkTransfer(false)}
          onDone={(moved) => {
            setShowBulkTransfer(false);
            setSelected(new Set());
            setBulkNotice(`${moved} ${moved === 1 ? 'bottle' : 'bottles'} moved`);
          }}
        />
      )}
    </div>
  );
}
